import React, { useState, useEffect } from 'react';
import { X, Gauge, Check, AlertTriangle } from 'lucide-react';
import RollingOdometerInput from '../shared/RollingOdometerInput';

/**
 * OdometerConfirmModal - Confirm/edit odometer reading
 * Used when:
 * - Starting work
 * - Vehicle selection
 * - Rest break (FRHV, if required by admin)
 * 
 * Shows last known reading, allows edit, requires confirmation
 */
const OdometerConfirmModal = ({ 
  isOpen, 
  onClose, 
  onConfirm,
  vehicle,
  source = 'WORK_START', // WORK_START, REST_BREAK, VEHICLE_SELECT
  loading = false
}) => {
  const [reading, setReading] = useState(0);
  const [isEdited, setIsEdited] = useState(false);
  
  const lastOdometer = vehicle?.last_odometer || 0;
  
  useEffect(() => {
    if (isOpen && vehicle) {
      setReading(lastOdometer);
      setIsEdited(false);
    }
  }, [isOpen, vehicle, lastOdometer]);
  
  const handleReadingChange = (newValue) => {
    setReading(newValue);
    setIsEdited(newValue !== lastOdometer);
  };
  
  const handleConfirm = () => {
    onConfirm({
      vehicle_id: vehicle.vehicle_id,
      confirmed_reading: reading,
      was_edited: isEdited,
      source
    });
  };
  
  if (!isOpen) return null;
  
  const isLowerThanLast = reading < lastOdometer;
  
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-2xl w-full max-w-md border border-slate-700 shadow-2xl">
        {/* Header */}
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <Gauge className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Confirm Odometer</h3>
              <p className="text-sm text-slate-400">{vehicle?.rego}</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-slate-700 rounded-lg transition"
            data-testid="odometer-modal-close"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>
        
        {/* Body */}
        <div className="p-4 space-y-4">
          {/* Last known info */}
          <div className="bg-slate-700/50 rounded-xl p-3">
            <div className="text-sm text-slate-400 mb-1">Last recorded reading:</div>
            <div className="text-xl font-mono font-bold text-white">
              {lastOdometer.toLocaleString()} km
            </div>
            {vehicle?.odometer_updated_at && (
              <div className="text-xs text-slate-500 mt-1">
                Updated: {new Date(vehicle.odometer_updated_at).toLocaleDateString()}
              </div>
            )}
          </div>
          
          {/* Rolling odometer input */}
          <RollingOdometerInput
            value={reading}
            onChange={handleReadingChange}
            label="Current Odometer Reading"
            digits={6}
          />
          
          {/* Warning if lower than last */}
          {isLowerThanLast && (
            <div className="flex items-start gap-2 p-3 bg-amber-500/20 border border-amber-500/50 rounded-xl">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-200">
                Reading is lower than last recorded. This may indicate an error or the odometer was reset.
              </div>
            </div>
          )}
          
          {/* Edited indicator */}
          {isEdited && !isLowerThanLast && (
            <div className="text-sm text-emerald-400 text-center">
              Reading modified from {lastOdometer.toLocaleString()} km
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-slate-700 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium transition"
            data-testid="odometer-modal-cancel"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="odometer-modal-confirm"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <Check className="w-5 h-5" />
                Confirm
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OdometerConfirmModal;
